import { CartUpdateEvent } from '@theme/events';

/**
 * Aromely cart cross-sell carousel + single add-to-cart path.
 *
 * Loaded with the cart drawer snippet AND the /cart page section so both
 * surfaces share one add implementation. The cart drawer JS
 * (`assets/aromely-cart-drawer.js`) calls `window.AromelyCrossSellInit()`
 * after every morph via `#reinitCrossSell()` — this file is what makes
 * that hook actually do something.
 *
 * Behaviors:
 *   - `window.AromelyCrossSellInit()` — destroys any existing Slyde and
 *     re-creates it against the current `[data-cross-sell-slyde]` element.
 *     Idempotent and safe to call when the container doesn't exist.
 *   - One-time delegated click handlers for card navigation and add-to-cart.
 *     The add requests rendered `sections` inline and dispatches a
 *     CartUpdateEvent carrying them, so the drawer/page morph in a single
 *     round trip. Buttons without `data-variant-id` (variant-modal triggers)
 *     are ignored here.
 *   - Drag detection so a swipe doesn't accidentally trigger a card click.
 */

(() => {
  /** @type {any} */
  let slydeInstance = null;

  const destroySlyde = () => {
    if (slydeInstance) {
      try { slydeInstance.destroy(); } catch { /* noop */ }
      slydeInstance = null;
    }
    const container = document.querySelector('[data-cross-sell-slyde]');
    if (container instanceof HTMLElement) delete container.dataset.initialized;
  };

  const initSlyde = () => {
    const container = document.querySelector('[data-cross-sell-slyde]');
    if (!(container instanceof HTMLElement)) return;
    if (container.dataset.initialized) return;

    // Slyde script may not be loaded yet — retry shortly.
    if (typeof Slyde === 'undefined') {
      setTimeout(initSlyde, 50);
      return;
    }

    // Container has zero width when the drawer hasn't laid out yet —
    // retry until it has dimensions.
    if (container.offsetWidth === 0) {
      setTimeout(initSlyde, 50);
      return;
    }

    container.dataset.initialized = 'true';
    const slideCount = container.querySelector('.slyde-wrapper')?.children.length || 0;

    // Cards stay at the design's ~138px regardless of how wide the strip is:
    // ~2.7 fit the drawer, ~2.4 the cart page's narrower phone column, more
    // fit the cart page's desktop column. The
    // fraction keeps one card peeking in so the strip reads as swipeable.
    const slidesPerView = Math.max(2.4, Math.round((container.offsetWidth / 146) * 10) / 10);

    try {
      slydeInstance = new Slyde.Slyde(container, {
        freeMode: true,
        slidesPerView,
        spaceBetween: 8,
        padding: slideCount > 1 ? { start: 16, end: 32 } : { start: 16, end: 16 },
        navigation: false,
        pagination: false,
        loop: false,
      });
    } catch (error) {
      console.error('Failed to initialize cross-sell Slyde:', error);
    }
  };

  // Public hook used by aromely-cart-drawer.js #reinitCrossSell().
  // Destroys + re-inits in one call so consumers don't have to know the
  // internal lifecycle.
  window.AromelyCrossSellInit = () => {
    destroySlyde();
    initSlyde();
  };

  // Also re-init when the cart drawer dialog opens (covers the case
  // where the drawer is closed/reopened without a morph in between).
  const wireDrawerObserver = () => {
    const drawer = document.querySelector('.aromely-cart-drawer__dialog');
    if (!drawer) {
      // Drawer might not be in the DOM yet — retry until the document is
      // fully loaded. Pages without a drawer (e.g. /cart) stop here instead
      // of retrying forever.
      if (document.readyState !== 'complete') requestAnimationFrame(wireDrawerObserver);
      return;
    }
    const observer = new MutationObserver(() => {
      if (/** @type {HTMLDialogElement} */ (drawer).open) {
        destroySlyde();
        initSlyde();
      }
    });
    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['open'],
    });
  };
  wireDrawerObserver();

  // Legacy `cart:update` event support — some integrations dispatch this.
  document.addEventListener('cart:update', () => {
    setTimeout(() => {
      destroySlyde();
      initSlyde();
    }, 100);
  });

  // Drag-vs-click discrimination so a swipe never triggers card navigation.
  let isDragging = false;
  let dragStartTime = 0;

  document.addEventListener('mousedown', (e) => {
    if (!(e.target instanceof Element) || !e.target.closest('[data-cross-sell-slyde]')) return;
    isDragging = false;
    dragStartTime = Date.now();
  });
  document.addEventListener('mousemove', (e) => {
    if (!(e.target instanceof Element) || !e.target.closest('[data-cross-sell-slyde]')) return;
    if (Date.now() - dragStartTime > 100) isDragging = true;
  });
  document.addEventListener('touchmove', (e) => {
    if (!(e.target instanceof Element) || !e.target.closest('[data-cross-sell-slyde]')) return;
    isDragging = true;
  });
  document.addEventListener('mouseup', () => {
    setTimeout(() => { isDragging = false; }, 10);
  });
  document.addEventListener('touchend', () => {
    setTimeout(() => { isDragging = false; }, 10);
  });

  // Card click → navigate to product page (skip if dragging or if click
  // landed on the add button).
  document.addEventListener('click', (e) => {
    if (isDragging) return;
    if (!(e.target instanceof Element)) return;

    const card = e.target.closest('.aromely-cross-sell__card');
    if (!(card instanceof HTMLElement)) return;
    if (e.target.closest('.aromely-cross-sell__add')) return;

    const productUrl = card.dataset.productUrl;
    if (productUrl) window.location.href = productUrl;
  });

  // Add-to-cart button (instant add). Variant-modal trigger buttons carry no
  // data-variant-id and are handled by their own component instead.
  document.addEventListener('click', async (e) => {
    if (isDragging) return;
    if (!(e.target instanceof Element)) return;

    const btn = e.target.closest('.aromely-cross-sell__add');
    if (!(btn instanceof HTMLButtonElement)) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    // A request is already in flight for this button (or a second copy of this
    // module saw the same click): never post the add twice.
    if (btn.disabled) return;

    e.stopPropagation();
    btn.disabled = true;
    btn.classList.add('is-loading');

    try {
      // Request the cart sections inline so listeners can morph without a
      // second network round trip.
      const sectionIds = [
        ...document.querySelectorAll(
          'aromely-cart-drawer-component, aromely-cart-page-component, cart-items-component'
        ),
      ]
        .map((el) => el.getAttribute('data-section-id'))
        .filter(Boolean);

      const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
          ...(sectionIds.length && {
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Add to cart failed');
      }
      const parsed = await response.json();

      let itemCount;
      for (const sectionId of sectionIds) {
        const html = parsed.sections?.[sectionId];
        if (!html) continue;
        const countText = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[ref="cartItemCount"]')?.textContent;
        if (countText) {
          itemCount = parseInt(countText, 10);
          break;
        }
      }

      document.dispatchEvent(
        new CartUpdateEvent(parsed, 'cross-sell', {
          itemCount,
          source: 'cross-sell',
          sections: parsed.sections,
        })
      );
    } catch (error) {
      // Say what happened where the shopper is looking (Shopify's description, e.g. "All 1 … are in your cart").
      const host = btn.parentElement || btn;
      let note = host.querySelector('.aromely-cross-sell__error');
      if (!note) {
        note = document.createElement('p');
        note.className = 'aromely-cross-sell__error';
        note.setAttribute('role', 'alert');
        btn.insertAdjacentElement('afterend', note);
      }
      const fallback = btn.dataset.errorText || document.querySelector('[data-cross-sell-error-text]')?.getAttribute('data-cross-sell-error-text') || "Couldn't add this. Please try again.";
      note.textContent = error instanceof Error && error.message !== 'Add to cart failed'
        ? error.message
        : fallback;
      window.setTimeout(() => note.remove(), 6000);
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
  });
})();
